import {
  ImageIcon,
  Moon,
  Sun,
  Search,
  Plus,
  Check,
  LayoutDashboard,
  Users,
  BarChart3,
  Settings,
} from 'lucide-react';
import { ThemeMode } from '@shigo-ui/theme';
import { readableBrandForeground } from '../../../../../../lib/networkBranding';
import { dashboardT } from '../../../../../../lib/messages';

const DEFAULT_SIDEBAR = '#141B2D';
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const demoNavItems = [
  { icon: LayoutDashboard, labelKey: 'networks.brand.demo.overview' },
  { icon: Users, labelKey: 'networks.brand.demo.supply' },
  { icon: BarChart3, labelKey: 'networks.brand.demo.reports' },
  { icon: Settings, labelKey: 'networks.brand.demo.settings' },
] as const;

export function NetworkBrandThemePreview({
  mode,
  logoUrl,
  accent,
  accentStrong,
  sidebarOverride,
}: {
  mode: ThemeMode.Light | ThemeMode.Dark;
  logoUrl: string;
  accent: string;
  accentStrong: string;
  sidebarOverride: string;
}) {
  const dark = mode === ThemeMode.Dark;
  const themeLabel = dashboardT(
    dark ? 'networks.brand.previewDark' : 'networks.brand.previewLight',
  );
  const page = dark ? '#090A0D' : '#F8F8FA';
  const surface = dark ? '#15151A' : '#FFFFFF';
  const muted = dark ? '#1A1B21' : '#F8F8FA';
  const border = dark ? '#24262D' : '#EBEBEB';
  const foreground = dark ? '#F4F6FB' : '#18181B';
  const secondaryForeground = dark ? '#A0A4B2' : '#71717B';
  const sidebar = previewColor(sidebarOverride, DEFAULT_SIDEBAR);
  const sidebarForeground = HEX_COLOR_PATTERN.test(sidebarOverride)
    ? readableBrandForeground(sidebar)
    : '#E8E4EE';
  const resolvedAccent = previewColor(accent, '#1A88E0');
  const resolvedAccentStrong = previewColor(accentStrong, '#1470C0');
  const Icon = dark ? Moon : Sun;

  return (
    <div key={mode} className="grid gap-2">
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {themeLabel}
      </div>
      <div
        role="group"
        aria-label={dashboardT('networks.brand.themePreview', { theme: themeLabel })}
        className="overflow-x-auto rounded-lg border"
        style={{ borderColor: border, backgroundColor: page, color: foreground }}
      >
        <div className="grid min-w-[520px] grid-cols-[128px_minmax(0,1fr)] text-xs">
          <aside
            aria-label={dashboardT('networks.brand.themeSidebar', { theme: themeLabel })}
            className="flex min-w-0 flex-col gap-6 p-3"
            style={{ backgroundColor: sidebar, color: sidebarForeground }}
          >
            <NetworkBrandLogoPreview
              {...{
                url: logoUrl,
                alt: dashboardT('networks.brand.themeLogoPreview', { theme: themeLabel }),
                className: 'h-5 w-20 self-start object-left',
                inverse: readableBrandForeground(sidebar) === '#FFFFFF',
              }}
            />
            <div className="grid gap-2">
              {demoNavItems.map(({ icon: NavIcon, labelKey }, index) => (
                <div
                  key={labelKey}
                  className="flex items-center gap-2 rounded-md px-2 py-2"
                  style={
                    index === 0
                      ? { backgroundColor: `color-mix(in srgb, ${resolvedAccent} 12%, ${sidebar})` }
                      : { opacity: 0.7 }
                  }
                >
                  <NavIcon className="size-3.5 shrink-0" aria-hidden="true" />
                  <span>{dashboardT(labelKey)}</span>
                </div>
              ))}
            </div>
            <span className="mt-auto opacity-60">
              {dashboardT('networks.brand.demo.workspace')}
            </span>
          </aside>
          <div className="min-w-0">
            <div
              className="flex items-center justify-between border-b px-4 py-3"
              style={{ borderColor: border, backgroundColor: surface }}
            >
              <span>{dashboardT('networks.brand.demo.dashboard')}</span>
              <span
                className="flex size-6 items-center justify-center rounded-full"
                style={{ backgroundColor: muted }}
              >
                <Users className="size-3" aria-hidden="true" />
              </span>
            </div>
            <div className="grid gap-4 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold">
                    {dashboardT('networks.brand.demo.overview')}
                  </h4>
                  <p className="mt-1" style={{ color: secondaryForeground }}>
                    {dashboardT('networks.brand.demo.period')}
                  </p>
                </div>
                <span
                  data-brand-preview="primary"
                  className="flex items-center gap-1 rounded-full px-3 py-2 font-medium"
                  style={{
                    backgroundColor: resolvedAccentStrong,
                    color: readableBrandForeground(resolvedAccentStrong),
                  }}
                >
                  <Plus className="size-3" aria-hidden="true" />
                  {dashboardT('networks.brand.demo.newReport')}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    ['networks.brand.demo.revenue', '$12,480'],
                    ['networks.brand.demo.impressions', '1.24M'],
                    ['networks.brand.demo.fill', '86.4%'],
                  ] as const
                ).map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-md border p-2.5"
                    style={{ borderColor: border, backgroundColor: surface }}
                  >
                    <p style={{ color: secondaryForeground }}>{dashboardT(label)}</p>
                    <p className="mt-1 text-sm font-semibold tabular-nums">{value}</p>
                  </div>
                ))}
              </div>
              <div
                className="overflow-hidden rounded-md border"
                style={{ borderColor: border, backgroundColor: surface }}
              >
                <div className="flex gap-4 border-b px-3" style={{ borderColor: border }}>
                  <span
                    data-brand-preview="accent"
                    className="border-b-2 py-2.5 font-medium"
                    style={{ borderColor: resolvedAccent }}
                  >
                    {dashboardT('networks.brand.demo.publishers')}
                  </span>
                  <span className="py-2.5" style={{ color: secondaryForeground }}>
                    {dashboardT('networks.brand.demo.placements')}
                  </span>
                </div>
                <div className="p-3">
                  <div
                    data-brand-preview="focus"
                    className="flex items-center gap-2 rounded-md border px-2 py-2"
                    style={{
                      borderColor: resolvedAccent,
                      boxShadow: `0 0 0 2px color-mix(in srgb, ${resolvedAccent} 18%, transparent)`,
                      color: secondaryForeground,
                    }}
                  >
                    <Search className="size-3.5" aria-hidden="true" />
                    {dashboardT('networks.brand.demo.search')}
                  </div>
                </div>
                <table className="w-full text-left text-xs">
                  <thead style={{ backgroundColor: muted, color: secondaryForeground }}>
                    <tr>
                      <th className="w-8 p-2">
                        <span className="sr-only">
                          {dashboardT('networks.brand.demo.selected')}
                        </span>
                      </th>
                      <th className="py-2 font-medium">
                        {dashboardT('networks.brand.demo.publisher')}
                      </th>
                      <th className="p-2 text-right font-medium">
                        {dashboardT('networks.brand.demo.impressions')}
                      </th>
                      <th className="p-2 text-right font-medium">
                        {dashboardT('networks.brand.demo.revenue')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Publisher 1', '624,180', '$6,240'],
                      ['Publisher 2', '412,600', '$4,120'],
                      ['Publisher 3', '203,220', '$2,120'],
                    ].map(([name, impressions, revenue], index) => (
                      <tr key={name} className="border-t" style={{ borderColor: border }}>
                        <td className="p-2">
                          <span
                            className="flex size-3.5 items-center justify-center rounded-sm border"
                            style={{
                              borderColor: index === 0 ? resolvedAccentStrong : border,
                              backgroundColor: index === 0 ? resolvedAccentStrong : surface,
                              color: readableBrandForeground(resolvedAccentStrong),
                            }}
                          >
                            {index === 0 ? <Check className="size-3" aria-hidden="true" /> : null}
                          </span>
                        </td>
                        <td className="py-2.5 font-medium">{name}</td>
                        <td className="p-2 text-right tabular-nums">{impressions}</td>
                        <td className="p-2 text-right tabular-nums">{revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function previewColor(value: string, fallback: string) {
  return HEX_COLOR_PATTERN.test(value) ? value.toUpperCase() : fallback;
}

export function NetworkBrandLogoPreview({
  url,
  alt,
  className,
  inverse = false,
}: {
  url: string;
  alt: string;
  className: string;
  inverse?: boolean;
}) {
  return url ? (
    <img
      src={url}
      alt={alt}
      className={`${className} max-w-full object-contain`}
      onError={(event) => {
        event.currentTarget.hidden = true;
      }}
      onLoad={(event) => {
        event.currentTarget.hidden = false;
      }}
    />
  ) : (
    <ImageIcon
      className={`${className} ${inverse ? 'text-white/55' : 'text-muted-foreground'}`}
      aria-hidden="true"
    />
  );
}
